import { useEffect, useState } from 'react';
import { LuBan, LuBookA, LuFolderPlus, LuMagnet, LuUser } from 'react-icons/lu';
import './App.css';
import styles from './App.module.css';
import {
  AuthPage,
  CreateProjectView,
  DevelopmentView,
  ProfileView,
  Sidebar,
  TranslationsView,
} from './components';
import { AppTextsProvider } from './contexts/AppTextsContext';
import { useAuth } from './contexts/AuthContext';
import { GlobalProvider } from './contexts/GlobalContext';
import { ProjectProvider, useProject } from './contexts/ProjectContext';
import Project from './entities/Project';
import { getEditorAppId } from './utils/editor';

export enum View {
  PROJECT = 'PROJECT',
  NEW_PROJECT = 'NEW_PROJECT',
  PROFILE = 'PROFILE',
  DEVELOPMENT = 'DEVELOPMENT',
  ERROR = 'ERROR',
}

export const ViewProperties = {
  [View.PROJECT]: { name: 'Project', icon: <LuBookA /> },
  [View.NEW_PROJECT]: { name: 'New Project', icon: <LuFolderPlus /> },
  [View.PROFILE]: { name: 'Profile', icon: <LuUser /> },
  [View.DEVELOPMENT]: { name: 'Development', icon: <LuMagnet /> },
  [View.ERROR]: { name: 'Error', icon: <LuBan /> },
} as const;

export type ViewProperty = (typeof ViewProperties)[View];

function Main() {
  const { project, initializeProject, setProject } = useProject();
  const [appId, setAppId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [view, setView] = useState<View | undefined>(undefined);

  const initialize = async () => {
    setIsLoading(true);
    const appId = await getEditorAppId();
    if (appId) {
      setAppId(appId);
      await initializeProject(appId);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    initialize();
  }, []);

  const projectUpdated = (updatedProject: Project) => {
    setProject(updatedProject);
  };

  const projectCreated = (updatedProject: Project) => {
    setProject(updatedProject);
  };

  useEffect(() => {
    if (view === undefined && project) {
      setView(View.PROJECT);
    } else if (view === View.NEW_PROJECT && project) {
      setView(View.PROJECT);
    } else if (view === undefined && !project) {
      setView(View.NEW_PROJECT);
    }
  }, [project]);

  return (
    <div className={styles.app}>
      <Sidebar currentView={view} viewChange={(view: View) => setView(view)} />
      {!isLoading && (
        <div className={styles.views}>
          {view === View.PROJECT && project && (
            <AppTextsProvider>
              <TranslationsView></TranslationsView>
            </AppTextsProvider>
          )}
          {view === View.NEW_PROJECT && appId && (
            <CreateProjectView appId={appId} projectCreated={projectCreated} />
          )}
          {view === View.PROFILE && <ProfileView />}
          {view === View.DEVELOPMENT && <DevelopmentView />}
          {view === View.ERROR && <p>Error</p>}
        </div>
      )}
    </div>
  );
}

function App() {
  const { appUser, loading } = useAuth();

  return (
    <>
      {appUser ? (
        <GlobalProvider>
          <ProjectProvider>
            <Main />
          </ProjectProvider>
        </GlobalProvider>
      ) : (
        <AuthPage />
      )}
    </>
  );
}

export default App;
